import { CustomerHandoffDialog } from '@/components/forms/Refunds/CustomerHandoffDialog'
import { RefundDestinationList } from '@/components/forms/Refunds/RefundDestinationList'
import {
  getRefundDestinationPreviewLines,
  getRefundDestinationTotals,
} from '@/components/forms/Refunds/refund-destinations'
import { CustomerNotificationSwitch } from '@/components/orders/CustomerNotificationSwitch'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { useAbility } from '@/lib/casl'
import { useRefundCashDrawer } from '@/lib/services/cash-drawers/useRefundCashDrawer'
import type { ExtraPaymentQuote } from '@/lib/services/refunds/refunds.types'
import { isPendingRefund, isUnsettledRefund } from '@/lib/services/refunds/refunds.utils'
import { useRefundExtraPaymentMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { formatCurrency, generateIdempotencyKey, getErrorMessage } from '@/lib/utils'
import { useState } from 'react'
import { toast } from 'sonner'

type ExtraPaymentRefundNoticeProps = {
  salesOrderId: number
  organizationId: number
  /** The order's location, for the till cash comes back from (`manualTenderLocationId`). */
  locationId: number
  extraPayment: ExtraPaymentQuote
  canManage: boolean
  /** The order's customer's email, which the refund receipt goes to. */
  customerEmail: string | null
}

/**
 * Payment an order change left above the order's total, when its refund didn't go through as the change saved:
 * staff return it here through the same refund flow.
 */
export function ExtraPaymentRefundNotice({
  salesOrderId,
  organizationId,
  locationId,
  extraPayment,
  canManage,
  customerEmail,
}: ExtraPaymentRefundNoticeProps) {
  const ability = useAbility()
  const refund = useRefundExtraPaymentMutation()
  const [isConfirmingHandoff, setIsConfirmingHandoff] = useState(false)
  // Set before the till is chosen, so a second click can't start another refund while it is.
  const [isRefunding, setIsRefunding] = useState(false)
  // One key per attempt, so a repeated click returns the same refund. A refund that didn't go through stays as it
  // is, so trying again is a new attempt; the server refuses it if the money already went back. A request whose
  // outcome is unknown (the response was lost) keeps its key: the retry resumes what the server may have accepted.
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey)
  // As the refund form offers: the customer gets the refund receipt unless staff turn it off.
  const [notifyCustomer, setNotifyCustomer] = useState(true)
  const sendCustomerEmail = Boolean(customerEmail) && notifyCustomer
  const total = extraPayment.total
  const { manualDestinationTotal, cardDestinationTotal } = getRefundDestinationTotals(
    extraPayment.refund_plan
  )
  const needsCashConfirmation = manualDestinationTotal > 0
  const { resolve: resolveRefundCashDrawer } = useRefundCashDrawer({
    organizationId,
    locationId,
    canOperateCashDrawers: ability.can('operate', 'cash-drawers'),
    enabled: needsCashConfirmation,
  })

  // The till cash comes back from is checked before staff are asked to hand it back, and again as the refund submits.
  const startHandoff = async () => {
    try {
      await resolveRefundCashDrawer()
      setIsConfirmingHandoff(true)
    } catch (error) {
      toast.error('Unable to refund the extra payment', { description: getErrorMessage(error) })
    }
  }

  const refundExtraPayment = async () => {
    setIsRefunding(true)
    try {
      const cashDrawerSessionId = needsCashConfirmation ? await resolveRefundCashDrawer() : null
      const response = await refund.mutateAsync({
        id: salesOrderId,
        expectedTotal: total,
        idempotencyKey,
        cashHandoffConfirmed: needsCashConfirmation,
        cashDrawerSessionId,
        sendCustomerEmail,
      })
      if (isUnsettledRefund(response.refund_status)) {
        setIdempotencyKey(generateIdempotencyKey())
        toast.error('The refund didn’t go through', {
          description: 'Review the refund on the order.',
        })
        return
      }
      if (isPendingRefund(response.refund_status)) {
        // The same refund, still on its way: a repeat resumes it rather than starting another.
        toast.success(`${formatCurrency(total)} refund is processing`)
        return
      }
      toast.success(`${formatCurrency(total)} refunded`)
    } catch (error) {
      toast.error('Unable to refund the extra payment', { description: getErrorMessage(error) })
    } finally {
      setIsRefunding(false)
      setIsConfirmingHandoff(false)
    }
  }

  return (
    <>
      <InfoNotice
        tone="warning"
        title={`${formatCurrency(total)} to refund`}
        body={
          <div className="space-y-2">
            <p>
              {extraPayment.eligible
                ? 'The customer paid more than this order’s total.'
                : extraPayment.ineligible_reason}
            </p>
            <RefundDestinationList
              lines={getRefundDestinationPreviewLines(extraPayment.refund_plan)}
            />
            {canManage && extraPayment.eligible && customerEmail ? (
              <CustomerNotificationSwitch
                notifyCustomer={notifyCustomer}
                onNotifyCustomerChange={setNotifyCustomer}
                disabled={isRefunding}
              />
            ) : null}
          </div>
        }
        action={
          canManage && extraPayment.eligible ? (
            <Button
              variant="notice"
              size="xs"
              disabled={isRefunding}
              onClick={() => (needsCashConfirmation ? startHandoff() : refundExtraPayment())}
            >
              {`Refund ${formatCurrency(total)}`}
            </Button>
          ) : null
        }
      />
      <CustomerHandoffDialog
        open={isConfirmingHandoff}
        onOpenChange={setIsConfirmingHandoff}
        manualTotal={manualDestinationTotal}
        electronicTotal={cardDestinationTotal}
        confirmLabel="Confirm Handoff & Refund"
        onConfirm={refundExtraPayment}
        isPending={isRefunding}
      />
    </>
  )
}
